import { Children, type FC, forwardRef, type ReactNode } from 'react';

import Spinner from '../Spinner';
import { StyledButton, StyledIconButton } from './Button.styled';
import type { ButtonProps, IconButtonProps } from './types';

const defaultProps = {
  color: 'primary',
  variant: 'solid',
  size: 'md',
  roundness: 'default',
} as const satisfies Pick<ButtonProps, 'color' | 'variant' | 'roundness' | 'size'>;

const isText = (child: ReactNode) => typeof child === 'string' || typeof child === 'number';

// Icons flank the label inside `children`; the loading state keeps only the label so the
// spinner takes the start icon's place and the end icon disappears, as the Figma state does.
function labelOnly(children: ReactNode): ReactNode {
  const items = Children.toArray(children);
  const first = items.findIndex(isText);
  if (first === -1) return items;

  const last = items.length - 1 - [...items].reverse().findIndex(isText);

  return items.slice(first, last + 1);
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      color = defaultProps.color,
      variant = defaultProps.variant,
      size = defaultProps.size,
      roundness = defaultProps.roundness,
      fullWidth = false,
      loading = false,
      loadingText,
      disabled,
      children,
      ...rest
    },
    ref,
  ) => (
    <StyledButton
      ref={ref}
      $color={color}
      $variant={variant}
      $size={size}
      $roundness={roundness}
      $fullWidth={fullWidth}
      $loading={loading}
      disabled={disabled || loading}
      focusableWhenDisabled={loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <>
          <Spinner color="inherit" />
          {loadingText ?? labelOnly(children)}
        </>
      ) : (
        children
      )}
    </StyledButton>
  ),
);

Button.displayName = 'Button';

const IconButton: FC<IconButtonProps> = ({
  color = defaultProps.color,
  variant = defaultProps.variant,
  size = defaultProps.size,
  roundness = defaultProps.roundness,
  selected,
  loading = false,
  loadingText,
  disabled,
  children,
  ...rest
}) => (
  <StyledIconButton
    $color={color}
    $variant={variant}
    $size={size}
    $roundness={roundness}
    $selected={selected ?? false}
    $loading={loading}
    aria-pressed={selected}
    disabled={disabled || loading}
    focusableWhenDisabled={loading}
    aria-busy={loading || undefined}
    {...rest}
    {...(loading && loadingText && { 'aria-label': loadingText })}
  >
    {loading ? <Spinner color="inherit" /> : children}
  </StyledIconButton>
);

IconButton.displayName = 'IconButton';

export { IconButton };
export default Button;
